import { useMemo } from 'react';
import { Box, Paper, Button, Alert, Typography, Skeleton } from '@mui/material';
import { useSiteEventsTimeline } from '../../../hooks/useSiteEventsTimeline';
import { groupTimelineByDate } from '../../../utils/dateGrouping';
import { TimeGroupHeader } from '../atoms/TimeGroupHeader';
import { EventRow } from '../molecules/EventRow';

export interface SiteEventsTimelineProps {
  siteId: number | null;
}

/**
 * Site events timeline organism
 *
 * Features:
 * - Aggregates events from site and all child entities (plots, labour items, allocations, variations)
 * - Grouped by date (Today, Yesterday, This Week, Older)
 * - Infinite scroll with "Load More" button
 * - Loading, error, and empty states
 * - Uses React Query for data fetching
 */
export function SiteEventsTimeline({ siteId }: SiteEventsTimelineProps) {
  const {
    events,
    totalCount,
    hasMore,
    isLoading,
    isLoadingMore,
    error,
    loadMore,
  } = useSiteEventsTimeline(siteId);

  // Group events by date
  const groupedEvents = useMemo(() => groupTimelineByDate(events), [events]);

  // Error state (show only if no data, otherwise show stale data)
  if (error && events.length === 0) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load site events: {error.message}
      </Alert>
    );
  }

  // Loading state (only when actually loading, not when disabled)
  if (isLoading && events.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Skeleton variant="rectangular" height={80} />
        <Skeleton variant="rectangular" height={80} />
        <Skeleton variant="rectangular" height={80} />
      </Box>
    );
  }

  // Empty state (no events after successful load)
  if (events.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          px: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No site events yet
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5 }}>
          Events will appear here as activity occurs across the site
        </Typography>
      </Box>
    );
  }

  // Timeline content
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Event groups */}
      {groupedEvents.map(group => (
        <Box key={group.dateGroup}>
          <TimeGroupHeader dateGroup={group.dateGroup} />
          <Paper
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            {group.events.map((event, index) => (
              <Box key={`${event.eventId}-${index}`}>
                <EventRow event={event} />
                {index < group.events.length - 1 && (
                  <Box
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      mx: 2,
                    }}
                  />
                )}
              </Box>
            ))}
          </Paper>
        </Box>
      ))}

      {/* Load More button */}
      {hasMore && (
        <Button
          variant="outlined"
          onClick={() => loadMore()}
          disabled={isLoadingMore}
          fullWidth
        >
          {isLoadingMore ? 'Loading...' : 'Load More Events'}
        </Button>
      )}

      {/* Summary text */}
      <Typography variant="caption" color="text.disabled" align="center">
        Showing {events.length} of {totalCount} events
      </Typography>
    </Box>
  );
}
